import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { adminFetch } from '../../lib/adminAuth';

const API = process.env.REACT_APP_API_URL || '';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdminBlogs() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${API}/api/admin/blogs`);
      const data = await res.json();
      if (data?.posts) setPosts(data.posts);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This can't be undone.`)) return;
    await adminFetch(`${API}/api/admin/blogs/${id}`, { method: 'DELETE' });
    setPosts(prev => prev.filter(p => p._id !== id));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <ArticleIcon sx={{ color: '#999' }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>Blog Posts</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#999' }}>Create and manage blog posts shown at /blogs</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/blogs/new')}
          sx={{ textTransform: 'none', bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#2a2a4e' } }}
        >
          New Post
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : posts.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, border: '1px solid #e8e8e8', borderRadius: 2, textAlign: 'center' }}>
          <ArticleIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
          <Typography sx={{ color: '#999', mb: 0.5 }}>No posts yet</Typography>
          <Typography variant="body2" sx={{ color: '#bbb', mb: 3 }}>Create your first blog post to get started.</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/blogs/new')}
            sx={{ textTransform: 'none', bgcolor: '#1a1a2e', '&:hover': { bgcolor: '#2a2a4e' } }}>
            New Post
          </Button>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ border: '1px solid #e8e8e8', borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Post</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Updated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map(post => (
                <TableRow key={post._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      {post.coverImage ? (
                        <Box component="img" src={post.coverImage} alt="" sx={{ width: 48, height: 48, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <Box sx={{ width: 48, height: 48, borderRadius: 1.5, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <ArticleIcon sx={{ color: '#ccc', fontSize: 22 }} />
                        </Box>
                      )}
                      <Box>
                        <Typography sx={{ fontWeight: 700, color: '#222', fontSize: 14 }}>{post.title}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#999' }}>/{post.slug}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={post.published ? 'Published' : 'Draft'}
                      size="small"
                      sx={{
                        bgcolor: post.published ? '#e8f5e9' : '#f5f5f5',
                        color: post.published ? '#2e7d32' : '#888',
                        fontWeight: 700, fontSize: 11,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#666', fontSize: 13 }}>{formatDate(post.updatedAt)}</TableCell>
                  <TableCell align="right">
                    {post.published && (
                      <IconButton size="small" component="a" href={`/blogs/${post.slug}`} target="_blank" rel="noopener noreferrer" title="View live">
                        <OpenInNewIcon fontSize="small" sx={{ color: '#888' }} />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => navigate(`/admin/blogs/${post._id}`)} title="Edit">
                      <EditIcon fontSize="small" sx={{ color: '#888' }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(post._id, post.title)} title="Delete">
                      <DeleteIcon fontSize="small" sx={{ color: '#c62828' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
